import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import { apiErrorMessage } from './prescriptionForm';
import {
  SHARED_WITH_COACH, COACH_CAN, NOT_SHARED,
  invitationsFrom, invitationHeading, answeredMessage, withoutInvitation,
} from './coachInvitations';
import '../styles/CoachInvitations.css';

// A coach has asked to coach you.
//
// # WHY THIS EXISTS
//
// Adding an athlete used to be something a coach did TO them: type an email,
// and that person's check-ins, training days and programmes appeared on the
// coach's overview. Registration is public, so that coach could be anybody. A
// link is now an invitation until the athlete answers it here.
//
// # WHY IT RENDERS NOTHING MOST OF THE TIME
//
// Almost every visit has no invitation, and a permanent "no invitations" card
// is noise on the screen an athlete opens to train. A failed lookup renders
// nothing too: an invitation that cannot be loaded now is still there next
// visit, and taking the training screen down over it would be the wrong trade.

const CoachInvitationsCard = () => {
  const { token } = useAuth();

  const [invitations, setInvitations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(buildApiUrl('/coach-invitations'), authConfig());
        if (live) setInvitations(invitationsFrom(data));
      } catch {
        if (live) setInvitations([]);
      }
    })();
    return () => { live = false; };
  }, [authConfig]);

  const answer = async (invitation, accept) => {
    setBusy(true);
    setNotice(null);
    try {
      if (accept) {
        await axios.post(
          buildApiUrl(`/coach-invitations/${invitation.id}/accept`), {}, authConfig()
        );
      } else {
        await axios.delete(buildApiUrl(`/coach-invitations/${invitation.id}`), authConfig());
      }
      setInvitations((list) => withoutInvitation(list, invitation.id));
      setNotice({ tone: 'success', message: answeredMessage(invitation, accept) });
    } catch (error) {
      setNotice({ tone: 'error', message: apiErrorMessage(error, 'That did not work. Try again.') });
    } finally {
      setBusy(false);
    }
  };

  // With no invitation and nothing just answered this renders an empty
  // fragment — no card, no heading, nothing to scroll past.
  return (
    <>
      {notice && (
        <p
          className={notice.tone === 'error' ? 'status-error' : 'status-success'}
          role={notice.tone === 'error' ? 'alert' : 'status'}
        >
          {notice.message}
        </p>
      )}
      {invitations.map((invitation) => (
        <section
          key={invitation.id}
          className="card coach-invitation"
          aria-labelledby={`coach-invitation-${invitation.id}`}
        >
          <h2 id={`coach-invitation-${invitation.id}`} className="section-title coach-invitation-title">
            {invitationHeading(invitation)}
          </h2>
          <p className="meta-copy">If you accept, they will see:</p>
          <ul className="body-copy coach-invitation-shared">
            {SHARED_WITH_COACH.map((line) => <li key={line}>{line}</li>)}
          </ul>
          <p className="meta-copy">{COACH_CAN} {NOT_SHARED}</p>
          <div className="coach-invitation-actions">
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => answer(invitation, true)}
            >
              Accept
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={() => answer(invitation, false)}
            >
              Decline
            </button>
          </div>
        </section>
      ))}
    </>
  );
};

export default CoachInvitationsCard;
